<template>
  <img
    v-if="image"
    :class="classes"
    :style="styles"
    :src="path"
    :height="h || size || 24"
    :width="w || size || 24"
  >
  <svg
    v-else-if="remixIcon"
    xmlns="http://www.w3.org/2000/svg"
    :viewBox="`0 0 ${w || 24} ${h || 24}`"
    :class="classes"
    :style="styles"
    :height="h || size || 24"
    :width="w || size || 24"
  >
    <use :xlink:href="require('remixicon/fonts/remixicon.symbol.svg') + `#ri-${remixIcon}`" />
  </svg>
  <svg
    v-else
    xmlns="http://www.w3.org/2000/svg"
    :viewBox="`0 0 ${w || 24} ${h || 24}`"
    :class="classes"
    :style="styles"
    :height="h || size || 24"
    :width="w || size || 24"
  >
    <path :d="path" />
  </svg>
</template>

<script>
/**
 * Provide a way to display icon with an SVG image and controls its aspect.
 *
 * @vue-prop {String} [color] - Applies specified color to the icon. Not working with 'image' prop.
 * @vue-prop {Boolean} [image] - Load an image instead of an SVG.
 * @vue-prop {Boolean} [left] - Places the icon on the left, when used inside a button.
 * @vue-prop {Boolean} [right] - Places the icon on the right, when used inside a button.
 * @vue-prop {Number} [size] - Specifies a custom size for the icon.
 * @vue-prop {Number} [w] - Specifies a custom width for the icon
 * @vue-prop {Number} [h] - Specifies a custom height for the icon
 * @vue-prop {Boolean} [noStyles] - Prevents applying hard styles to the element
 * @vue-prop {String} [remixIcon] - If set, use a remix library icon
 *
 * @vue-slot default - SVG path/Image source.
 */
export default {
  name: 'Icon',

  props: {
    color: {
      type: String,
      default: null,
    },
    image: Boolean,
    left: Boolean,
    right: Boolean,
    rotateLeft: Boolean,
    rotateRight: Boolean,
    size: {
      type: Number,
      default: 0,
      validator: v => v >= 0,
    },
    w: {
      type: Number,
      default: 0,
      validator: v => v >= 0,
    },
    h: {
      type: Number,
      default: 0,
      validator: v => v >= 0,
    },
    noStyles: Boolean,
    remixIcon: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      path: '',
    };
  },

  computed: {
    classes() {
      return {
        icon: true,
        'icon--left': !!this.left,
        'icon--right': !!this.right,
        'icon--rotate-left': this.rotateLeft,
        'icon--rotate-right': this.rotateRight,
      };
    },
    styles() {
      if (this.noStyles) {
        return {};
      }
      const styles = { color: this.color };
      if (this.size) {
        styles.height = `${this.size}px`;
        styles.width = `${this.size}px`;
      }
      if (this.w) {
        styles.width = `${this.w}px`;
      }
      if (this.h) {
        styles.height = `${this.h}px`;
      }
      return styles;
    },
  },

  mounted() {
    this.updatePath();
  },

  updated() {
    this.updatePath();
  },

  methods: {
    updatePath() {
      if (
        !this.$scopedSlots
        || !(this.$scopedSlots.default instanceof Function)
      ) {
        return;
      }
      this.path = this.$scopedSlots
        .default()
        .map(n => n.text)
        .join('');
    },
  },
};
</script>

<style lang="scss">
.icon {
  height: 24px;
  width: 24px;
  fill: currentColor;

  &--left {
    margin-right: 6px;
  }
  &--right {
    margin-left: 6px;
  }

  &--rotate-right {
    transform: rotate(90deg);
  }

  &--rotate-left {
    transform: rotate(-90deg);
  }
}
</style>
